import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonRemoveAlt1Icon from '@mui/icons-material/PersonRemoveAlt1';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import TuneIcon from '@mui/icons-material/Tune';
import {
  ActivityHistoryCard,
  ActivityHistoryDayGroup,
  ActivityHistoryDialog,
  ActivityHistoryEmpty,
  ActivityHistoryFilterBar,
  ActivityHistorySelect,
  ActivityHistorySkeleton,
} from '../../../Components/ActivityHistory/ActivityHistoryUi';
import { getCollectionActivity } from '../../../services/collectionActivityService';
import {
  ACTIVITY_ROLE_FILTER_OPTIONS,
  ACTIVITY_TIME_RANGE_OPTIONS,
  filterCollectionActivityRows,
  formatCollectionActivityRow,
  getCollectionActivityOpenTarget,
  groupCollectionActivityByDay,
} from '../../utils/collectionActivityDisplay';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';

const PAGE_SIZE = 20;

const ICON_BY_ACTION = {
  created: PlaylistAddIcon,
  renamed: DriveFileRenameOutlineIcon,
  shared: LinkIcon,
  unshared: LinkOffIcon,
  share_updated: TuneIcon,
  member_added: PersonAddAltIcon,
  member_removed: PersonRemoveAlt1Icon,
  item_updated: EditOutlinedIcon,
};

function readRows(response) {
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function ActivityEventCard({ row, expanded, onToggle, onOpenItem }) {
  const display = formatCollectionActivityRow(row);
  const summary = display.changes.length === 1
    ? display.changes[0].summary
    : `${display.changes.length} cambios`;
  const openTarget = getCollectionActivityOpenTarget(row);
  const canOpen = Boolean(openTarget && onOpenItem);
  return (
    <ActivityHistoryCard
      icon={ICON_BY_ACTION[row.action] || HistoryOutlinedIcon}
      title={display.title}
      time={display.timeShort}
      actor={display.actor}
      roleKey={display.roleKey}
      roleLabel={display.roleLabel}
      summary={summary}
      details={display.changes.map((change) => ({
        key: `${change.field}-${change.from}-${change.to}`,
        text: `${change.field}: ${change.from} → ${change.to}`,
      }))}
      expanded={expanded}
      onToggle={onToggle}
      onOpen={canOpen ? () => onOpenItem(openTarget) : null}
    />
  );
}

/**
 * Bitácora de una colección. Dueño y editor.
 */
export default function CollectionActivityDialog({
  open,
  kind = 'list',
  collectionId,
  collectionName = '',
  onClose,
  onOpenItem,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState('');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [timeRange, setTimeRange] = useState('7d');
  const [roleFilter, setRoleFilter] = useState('all');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const { scrollRef: fadeRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();
  const setListNode = useCallback((node) => {
    listRef.current = node;
    fadeRef(node);
  }, [fadeRef]);

  useEffect(() => {
    if (!open || !collectionId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setRows([]);
    setHasMore(false);
    setNextBefore('');
    setQuery('');
    setTimeRange('7d');
    setRoleFilter('all');
    setExpandedIds(new Set());
    (async () => {
      try {
        const query = kind === 'cad'
          ? { projectId: collectionId, limit: PAGE_SIZE }
          : { listId: collectionId, limit: PAGE_SIZE };
        const res = await getCollectionActivity(query, { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (res._httpStatus === 404 || !res._httpOk) {
          setError(res.error || res.message || 'No se pudo cargar el historial.');
          return;
        }
        setRows(readRows(res));
        setHasMore(res.has_more === true);
        setNextBefore(String(res.next_before || '').trim());
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Historial: GET falló', err);
        setError('No se pudo cargar el historial.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [open, kind, collectionId]);

  const handleLoadMore = useCallback(async () => {
    if (!collectionId || !hasMore || !nextBefore || loadingMore || loading) return;
    setLoadingMore(true);
    setError('');
    try {
      const query = kind === 'cad'
        ? { projectId: collectionId, limit: PAGE_SIZE, before: nextBefore }
        : { listId: collectionId, limit: PAGE_SIZE, before: nextBefore };
      const res = await getCollectionActivity(query);
      if (!res._httpOk) {
        setError(res.error || res.message || 'No se pudo cargar el historial.');
        setHasMore(false);
        return;
      }
      const next = readRows(res);
      setRows((prev) => [...prev, ...next]);
      setHasMore(res.has_more === true);
      setNextBefore(String(res.next_before || '').trim());
    } catch (err) {
      console.error('Historial: GET más falló', err);
      setError('No se pudo cargar el historial.');
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [collectionId, hasMore, kind, loading, loadingMore, nextBefore]);

  const filteredRows = useMemo(
    () => filterCollectionActivityRows(rows, {
      query,
      timeRange,
      role: roleFilter,
    }),
    [rows, query, timeRange, roleFilter],
  );
  const dayGroups = useMemo(
    () => groupCollectionActivityByDay(filteredRows),
    [filteredRows],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!open || !sentinel || !hasMore || loading || loadingMore || error) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) handleLoadMore();
      },
      { root, rootMargin: '96px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, loading, loadingMore, error, handleLoadMore, filteredRows.length]);
  useEffect(() => {
    refreshScrollEdges();
  }, [filteredRows, expandedIds, loading, loadingMore, refreshScrollEdges]);

  const toggleExpanded = (rowId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const filtersActive = Boolean(query.trim()) || timeRange !== '7d' || roleFilter !== 'all';
  const emptyMessage = rows.length === 0
    ? 'Todavía no hay actividad en esta lista.'
    : (filtersActive
      ? 'No hay eventos con estos filtros.'
      : 'No hay eventos en los últimos 7 días.');

  return (
    <ActivityHistoryDialog
      open={open}
      onClose={onClose}
      subtitle={collectionName
        ? `Cambios y acciones sobre la lista ${collectionName}.`
        : 'Cambios y acciones realizadas sobre esta lista.'}
      error={error}
      listRef={setListNode}
      listSx={getScrollEdgeFadeSx(fadeTop, fadeBottom)}
      busy={loading || loadingMore}
      filters={(
        <ActivityHistoryFilterBar
          query={query}
          onQueryChange={setQuery}
          placeholder="Buscar por lote, acción o email"
        >
          <ActivityHistorySelect
            value={roleFilter}
            onChange={setRoleFilter}
            options={ACTIVITY_ROLE_FILTER_OPTIONS}
            ariaLabel="Filtrar por rol"
            sx={{ minWidth: 112 }}
          />
          <ActivityHistorySelect
            value={timeRange}
            onChange={setTimeRange}
            options={ACTIVITY_TIME_RANGE_OPTIONS}
            ariaLabel="Filtrar por período"
          />
        </ActivityHistoryFilterBar>
      )}
    >
      {loading ? (
        <ActivityHistorySkeleton />
      ) : filteredRows.length === 0 && !error ? (
        (hasMore || loadingMore) ? (
          <ActivityHistorySkeleton count={3} />
        ) : (
          <ActivityHistoryEmpty
            message={emptyMessage}
            actionLabel={rows.length > 0 && timeRange !== 'all' ? 'Ver todo el tiempo' : ''}
            onAction={() => setTimeRange('all')}
          />
        )
      ) : (
        dayGroups.map((group) => (
          <ActivityHistoryDayGroup key={group.key} label={group.label}>
            {group.rows.map((row, index) => {
              const rowId = row.id || `${row.created_at}-${row.action}-${index}`;
              return (
                <ActivityEventCard
                  key={rowId}
                  row={row}
                  expanded={expandedIds.has(rowId)}
                  onToggle={() => toggleExpanded(rowId)}
                  onOpenItem={onOpenItem}
                />
              );
            })}
          </ActivityHistoryDayGroup>
        ))
      )}
      {hasMore ? <Box ref={sentinelRef} sx={{ height: 1 }} /> : null}
      {loadingMore && filteredRows.length > 0 ? (
        <ActivityHistorySkeleton count={1} />
      ) : null}
    </ActivityHistoryDialog>
  );
}
